<template>
  <div
    class="table-responsive"
    data-sb-regression="sb-table-expandable-rows"
    :class="overflowClass"
  >
    <!-- @slot Slot to show the Toolbar -->
    <slot name="sb-toolbar" />
    <!-- @slot Slot to show the Tabs -->
    <slot name="sb-tabs" />
    <!-- @slot Slot to show the Banner -->
    <slot name="sb-banner" />
    <div
      ref="tableScrollContainerRef"
      data-test-id="sb-table-wrapper"
      :class="[cardStyle ? 'sb-table-card-wrapper' : 'sb-table-base-wrapper', overflowClass]"
    >
      <!-- @slot Slot to show the Filters -->
      <slot name="sb-filters" />
      <SbLoadingPromiseWrapper
        v-model:loading="loading"
        size="xtsm"
        :loading-promise="loadingPromise"
        :classes="classes"
        @scroll.passive="handleScrollStickyHeaders"
      >
        <table
          :aria-label="$t('sbTable.shipbobTable')"
          class="sb-table-expandable-rows"
          :class="[variation]"
        >
          <template v-if="$slots['sb-table-header']">
            <!-- @slot Slot to show the Table Header -->
            <slot
              name="sb-table-header"
              :cols="columnDefinitions"
              :class="{
                'sticky-table-header ': useStickyHeaders,
                'sticky-table-header-on-scroll': useStickyHeaders && isTableScrolled
              }"
            />
          </template>
          <SbTableExpandableHeader
            v-else
            :table="expandableTable"
            :table-has-sections="tableHasSections"
            :column-events="columnEvents"
            :show-action-bar="showActionBar"
            :table-can-expand="canExpand"
            :sub-rows-table-selectable="subRowsTableSelectable != null"
            :on-row-selected="onRowSelected"
            :api-sorting="apiSorting"
            :all-columns-are-sortable="allColumnsAreSortable"
            :class="{
              'sticky-table-header ': useStickyHeaders,
              'sticky-table-header-on-scroll': useStickyHeaders && isTableScrolled
            }"
            @api-sort="emitApiSort"
          >
            <template
              v-if="$slots['sb-table-action-bar']"
              #sb-table-action-bar
            >
              <!-- @slot Passing ActionBar slot to Header component -->
              <slot
                name="sb-table-action-bar"
                :rows="expandableTable.getSelectedRowModel()"
              />
            </template>
          </SbTableExpandableHeader>
          <tbody v-if="loading">
            <tr>
              <td :colspan="columnDefinitions.length">
                <!-- @slot Slot to show the Loading State -->
                <slot
                  v-if="$slots['sb-table-loading-state']"
                  name="sb-table-loading-state"
                />
                <span
                  v-else
                  class="no-rows"
                >
                  <sb-text value="common.fetchingData" />...
                </span>
              </td>
            </tr>
          </tbody>
          <tbody v-if="!loading && data.length == 0">
            <tr>
              <td :colspan="emptyTableColSpan">
                <!-- @slot Slot to show the Table Empty State -->
                <slot
                  v-if="$slots['sb-table-empty-state']"
                  name="sb-table-empty-state"
                />
                <span
                  v-else
                  class="no-rows"
                >
                  <sb-text value="sbTable.noRows" />...
                </span>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <template
              v-for="(row, index) in expandableTable?.getRowModel().rows"
              :key="row.id"
            >
              <slot
                v-if="$slots['sb-table-row']"
                name="sb-table-row"
                :row="row"
                :index="index"
                :on-row-selected="onRowSelected"
                :expand-collapse-component="expandCollapseComponent"
              />
              <SbTableExpandableBaseRow
                v-else-if="displayBaseRow(row)"
                :table="expandableTable"
                :row="row"
                :on-row-selected="onRowSelected"
                :enable-row-selection="expandableTable?.options?.enableRowSelection"
                :table-can-expand="canExpand"
                :subrow-can-expand="row.original.useCustomSubRow"
                :column-definitions="columnDefinitions"
                :row-events="rowEvents"
                :sub-rows-table-selectable="subRowsTableSelectable != null"
                :subrow-column-definitions="subrowColumnDefinitions"
              />
              <tr
                v-if="row.getIsExpanded() && (row.original.useCustomSubRow || useSubRowTable(row))"
                class="expandable-rows-table-subrow"
              >
                <td :colspan="columnDefinitions?.length">
                  <div class="expandable-row-subtable">
                    <template v-if="row.original.useCustomSubRow">
                      <!-- @slot Slot to show custom subrow. When provided, row.useCustomSubRow should be true -->
                      <slot
                        name="sb-table-subrow"
                        :row="row"
                        :index="index"
                        :on-row-selected="onRowSelected"
                      />
                    </template>
                    <SbExpandableSubTableRow
                      v-else-if="useSubRowTable(row)"
                      :row="row"
                      :on-row-selected="onRowSelected"
                      :column-definitions="columnDefinitions"
                      :subrow-column-definitions="subrowColumnDefinitions"
                      :sub-rows-table-selectable="subRowsTableSelectable"
                      :override-parent-on-off="overrideParentOnOff"
                      :indeterminate-default-selected="indeterminateDefaultSelected"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
          <tfoot v-if="tableHasSections.hasFooter">
            <tr
              v-for="footerGroup in expandableTable?.getFooterGroups()"
              :key="footerGroup.id"
              v-memo="[footerGroup]"
              :class="[footerGroup?.class ?? '']"
            >
              <th
                v-for="header in footerGroup.headers"
                :key="header.id"
                :colSpan="header.colSpan"
                :class="[header.column?.columnDef?.class?.footer ?? '']"
                :style="{ width: header.getSize() + 'px' }"
                scope="col"
              >
                <FlexRender
                  v-if="!header.isPlaceholder"
                  :render="header.column?.columnDef?.footer"
                  :props="header.getContext()"
                />
              </th>
            </tr>
          </tfoot>
        </table>
      </SbLoadingPromiseWrapper>
      <sb-pagination
        v-if="showPagination"
        key="ts"
        v-bind="pagination"
        @fetch-page="handleFetchPage"
      >
        <template #pagination-links>
          <sb-pagination-links :range="pagination.range">
            <template
              v-if="!pagination.hidePrevious"
              #page-link-previous
            >
              <sb-pagination-link><sb-text value="Previous" /></sb-pagination-link>
            </template>
            <template
              v-if="!pagination.hideLinks"
              #page-links="{ page }"
            >
              <sb-pagination-link>{{ page }}</sb-pagination-link>
            </template>
            <template
              v-if="!pagination.hideNext"
              #page-link-next
            >
              <sb-pagination-link><sb-text value="Next" /></sb-pagination-link>
            </template>
          </sb-pagination-links>
        </template>
        <template
          v-if="!pagination.hideText"
          #pagination-text="{ totalPages, totalRecords }"
        >
          <sb-pagination-text>
            <sb-text
              value="sbPagination.pagination"
              :params="{ currentPage: pagination.currentPage, totalPages, visibleRecordsInterval, totalRecords}"
            />
          </sb-pagination-text>
        </template>
        <template
          v-if="!pagination.hideInput"
          #pagination-input
        >
          <sb-pagination-input label-text="Go to page: " />
        </template>
      </sb-pagination>
      <!-- @slot Slot to show elements below table, but inside the card -->
      <slot
        v-if="cardStyle"
        name="sb-footer"
      />
    </div>
  </div>
</template>

<script>
import { FlexRender } from '@tanstack/vue-table';
import SbLoadingPromiseWrapper from '@src/components/Loading/SbLoadingPromiseWrapper.vue';
import SbText from '@src/components/Text/SbText.vue';
import SbPagination from '@src/components/Pagination/SbPagination.vue';
import SbPaginationLink from '@src/components/Pagination/SbPaginationLink.vue';
import SbPaginationText from '@src/components/Pagination/SbPaginationText.vue';
import SbPaginationLinks from '@src/components/Pagination/components/SbPaginationLinks.vue';
import SbPaginationInput from '@src/components/Pagination/components/SbPaginationInput.vue';
import DeprecationTrackingMixin from '@src/utils/mixins/deprecatedComponentTracking';
import debounce from 'lodash/debounce';
import { createExpandableRowsTable, tableHasSections } from '@src/components/Table/components/SbTableExpandableRows/createTable';
import SbTableExpandableHeader from '@src/components/Table/components/SbTableExpandableHeader.vue';
import SbTableExpandableBaseRow from '@src/components/Table/components/SbTableExpandableBaseRow.vue';
import SbExpandableSubTableRow from '@src/components/Table/components/SbExpandableSubTableRow.vue';
import { selectableProp } from '@src/components/Table/utils/selectableMixin';
import ExpandCollapseRowCell from '@src/components/Table/components/SbTableExpandableRows/components/ExpandCollapseRowCell.vue';
import { SORTING_ON_HOVER_COLUMN_COUNT_RATE } from '@src/components/Table/constants';

export default {
  name: 'SbTableExpandableRows',
  components: {
    FlexRender,
    SbLoadingPromiseWrapper,
    SbText,
    SbPagination,
    SbPaginationLink,
    SbPaginationText,
    SbPaginationLinks,
    SbPaginationInput,
    SbTableExpandableHeader,
    SbTableExpandableBaseRow,
    SbExpandableSubTableRow,
  },
  mixins: [DeprecationTrackingMixin],
  provide() {
    const { sbTableSort } = this;
    return {
      sbTableSort,
      sortTable: this.sortTable,
    };
  },
  props: {
    /**
     * The data corresponding to the rows of the table
     * To add sub rows to the table add subRows as a property in your data which will contain the data for the sub rows.
     */
    data: {
      type: Array,
      default: () => ([]),
    },
    /**
     * The promise to show loading indicator, removes indicator when promise is resolved or rejected
     */
    loadingPromise: {
      type: Promise,
      default: null,
    },
    /**
    * Same as the column definitions but applicable only to subrows.
    * This allows us to render the first level of subrows as an sb-table.
    * Multiple levels of subrows as tables is not supported
    * Different column definitions can be used for different rows.
    * In this case the subrowColumnDefinitions should be an array of arrays of column definitions.
    * The row object should specify which subrowColumnDefinitions to use by adding a subrowColumnDefinitionsToUse
    * property to the row object. This property should be the index of the subrowColumnDefinitions array.
    */
    subrowColumnDefinitions: {
      required: false,
      type: Array,
      default: () => [],
    },
    /**
    * The column definitions required for the table.
    * available column properties. Objects containing any of the following:
    * header,
    * footer,
    * id,
    * cell,
    * class:{
    *    header, //applied to the th for that column
    *    cell, //applied to the td for that column
    *    footer, //applied to the td for that column
    * },
    * size,
    * minSize,
    * maxSize,
    * colSpan,
    * inputType, //Any html5 input type https://www.w3schools.com/html/html_form_input_types.asp
    * inputAttributes, //Attributes to apply to the edit input element
    * columns,
    */
    columnDefinitions: {
      required: true,
      type: Array,
    },
    /**
     * Used to set the type of selection strategy
     */
    selectable: selectableProp(),
    /**
     * Style variation to give additional meaning.
     * `primary,  minimal
     */
    variation: {
      type: String,
      default: 'primary',
      validator: (value) => value.match(/(primary|minimal|)/),
    },
    /**
     * Collection of IDs that should be selected in the table at render
     */
    preselectedKeys: {
      type: Array,
      default: () => [],
    },
    /**
     * Whether to show the action bar for the table.
     * Note: Action bar needs to be provided via the slot, there is no default action bar.
     */
    showActionBar: {
      type: Boolean,
      default: false,
    },
    /**
    * Whether the rows can be expanded or not.
    */
    canExpand: {
      type: Boolean,
      default: true,
    },
    /**
     * Used to set the type of selection strategy for a nested table
     */
    subRowsTableSelectable: selectableProp(),
    /**
    * Pagination object with default values
    *  range: 3,
    *  pageSize: 10,
    *  currentPage: 1,
    *  totalPages: 2,
    *  totalRecords: 0,
    *  visibleRecords: 0,
    *  hidePrevious: false,
    *  hideLinks: false,
    *  hideNext: false,
    *  hideText: false,
    *  hideInput: false,
     */
    pagination: {
      type: Object,
      default: () => ({
        range: 3,
        pageSize: 10,
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        visibleRecords: 0,
        hidePrevious: false,
        hideLinks: false,
        hideNext: false,
        hideText: false,
        hideInput: false,
      }),
    },
    /**
    * Whether pagination should be displayed or not.
    */
    showPagination: {
      type: Boolean,
      default: true,
    },
    /**
    * If sorting logic will be done by an API.
    */
    apiSorting: {
      type: Boolean,
      default: false,
    },
    /**
    * For SubTables, this flag indicates if a parent row should be overriden as selected/unselected when all or none subrows are selected
    * */
    overrideParentOnOff: {
      type: Boolean,
      default: false,
    },
    /**
    * For SubTables, this flag indicates the selected state to override a parent row if some but not all subrows are selected.
    * Leave as Null for no override.
    * Requires overrideParentOnOff to be set to true.
    */
    indeterminateDefaultSelected: {
      type: Boolean,
      default: null,
    },
    /**
    * Activates table card style.
    */
    cardStyle: {
      type: Boolean,
      default: false,
    },
    /**
    * Disables overflow classes.
    */
    disableOverflowClass: {
      type: Boolean,
      default: false,
    },
    /**
     * If the headers should be sticky on overflow
     */
    useStickyHeaders: {
      type: Boolean,
      default: false,
    },

    /**
     * Range: [0, 100]
     * Minimum sorting-enabled columns count percentage out of total columns count to
     * display sorting icons only on hover
    */
    minSortingIconVisibleOnHoverPercentage: {
      type: Number,
      default: SORTING_ON_HOVER_COLUMN_COUNT_RATE,
    },
  },
  emits: [
    /**
     * Emitted when the main table is selectable and the checkbox value changes.
     */
    'row-selection-updated',
    /**
     * Emitted when there is a selectable table in the expandable row, and one of those rows are selected.
    */
    'sub-table-row-selection-updated',
    /**
    * Wildcard emit that can be attached to a custom column header cell to pass events to parent components.
    * On the <sb-expandable-table> component add any :on-column-**** listener and it will be assigned to all VueCell columns
    * @param {Object}
    */
    'column-*',
    /**
    * Wildcard emit that can be attached to a custom data row cell to pass events to parent components.
    * On the <sb-expandable-table> component add any :on-row-**** listener and it will be assigned to all VueCell rows
    * @param {Object}
    */
    'row-*',
    /**
     * Emitted when the user interacts with pagination.
     */
    'fetch-page',
    /**
    * Only emmited when apiSorting is true.
    */
    'api-sort',
  ],
  data: () => ({
    tableHasSections,
    loading: false,
    sbTableSort: {
      sortColumn: null,
      descending: true,
    },
    allColumnsAreSortable: true,
    expandCollapseComponent: ExpandCollapseRowCell,
    isTableScrolled: false,
  }),
  computed: {
    expandableTable() {
      return createExpandableRowsTable({
        data: this.data,
        columns: this.columnDefinitions || [],
        eventCallbacks: {
          onRowSelected: this.onRowSelected,
        },
        preselectedKeys: this.preselectedKeys,
        selectionType: this.selectable,
        minSortingIconVisibleOnHoverPercentage: this.minSortingIconVisibleOnHoverPercentage,
        apiSorting: this.apiSorting,
      });
    },
    columnEvents() {
      return Object.fromEntries(
        Object.entries(this.$attrs)
          .filter(([key]) => key.startsWith('onColumn')),
      );
    },
    rowEvents() {
      return Object.fromEntries(
        Object.entries(this.$attrs)
          .filter(([key]) => key.startsWith('onRow')),
      );
    },
    overflowClass() {
      if (this.disableOverflowClass) return '';
      return this.loading ? 'overflow-hidden' : 'overflow-auto';
    },
    classes() {
      return this.loading ? ['sb-table-wrapper'] : ['sb-table-wrapper', 'sb-table-custom-scroll'];
    },
    emptyTableColSpan() {
      const span = this.columnDefinitions?.length;
      return this.selectable ? span + 1 : span;
    },
    visibleRecordsInterval() {
      // calculate min item based on current page
      let startItem = ((this.pagination.currentPage - 1) * this.pagination.pageSize) + 1;
      // calculate last item based on current page
      let endItem = this.pagination.currentPage * this.pagination.pageSize;

      // update last item count based on total records
      if (endItem > this.pagination.totalRecords) {
        endItem = this.pagination.totalRecords;
      }

      // edge case when there is no data
      if (startItem > endItem) {
        startItem = 0;
      }

      return `${startItem} - ${endItem}`;
    },
  },
  created() {
    this.tableHasSections.hasHeader = false;
    this.tableHasSections.hasFooter = false;

    this.allColumnsAreSortable = !this.expandableTable.getFlatHeaders().some((header) => {
      return !header.column.columnDef.enableSorting;
    });
  },
  methods: {
    onRowSelected() {
      if (!this.subRowsTableSelectable) {
        // without subtable, output everything
        const { flatRows } = this.expandableTable.getSelectedRowModel();
        this.$emit('row-selection-updated', flatRows.map((r) => r.original));
      } else {
        // if subtable selectable, break items into depths
        const { flatRows } = this.expandableTable.getSelectedRowModel();
        const topLevel = flatRows.filter((x) => x.depth === 0).map((r) => {
          return {
            ...r.original,
            selectedSubrows: r.subRows?.filter((sr) => sr.getIsSelected()).map((sr) => sr.original),
          };
        });

        const subtableLevel = flatRows.filter((x) => x.depth !== 0).map((r) => r.original);
        this.$emit('row-selection-updated', topLevel);
        this.$emit('sub-table-row-selection-updated', subtableLevel);
      }
    },
    sortTable(sortColumn) {
      this.sbTableSort.sortColumn = sortColumn;
      const accessorKey = sortColumn.accessorKey?.replace(/\./g, '_');

      const column = this.expandableTable.getColumn(sortColumn.id ?? accessorKey);
      const currentSortDirection = column.getIsSorted();

      if (currentSortDirection === 'asc') {
        column.toggleSorting(true);
      } else {
        column.toggleSorting();
      }

      this.sbTableSort.descending = column.getIsSorted() && column.getIsSorted() === 'desc';
    },
    handleFetchPage(fetchPageEvent) {
      this.$emit('fetch-page', fetchPageEvent);
    },
    emitApiSort(item) {
      this.$emit('api-sort', item);
    },
    displayBaseRow(row) {
      // display base row if no subrow table OR first item of subrow table
      return ((this.subrowColumnDefinitions || []).length === 0 && !this.$slots['sb-table-subrow']) || row?.depth === 0;
    },
    useSubRowTable(row) {
      // has subrow columns
      return (this.subrowColumnDefinitions?.length
                 && row?.subRows?.length
      );
    },
    handleScrollStickyHeaders: debounce(
      function handleScrollStickyHeaders() {
        this.isTableScrolled = this.$refs?.tableScrollContainerRef?.querySelector('.sb-table-wrapper').scrollTop > 0 || 0;
      },
      50,
    ),
  },
};
</script>

<style lang="scss">
@import '@src/components/Table/style/sb-table-expandable-rows.scss';
@import '@src/components/Table/style/sb-table-variations.scss';
</style>
